import { Link } from "react-router-dom";
import {
  ArrowRight, Newspaper, Calendar, IdCard, BarChart3, Award, Shield,
  Users, Lock, FileBadge, Globe2, CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { MarketingNav } from "@/components/Marketing/MarketingNav";
import { MarketingFooter } from "@/components/Marketing/MarketingFooter";
import { MarketingFinalCta } from "@/components/Marketing/MarketingFinalCta";
import bentoRegistry from "@/assets/bento-registry.jpg";
import bentoEvents from "@/assets/bento-events.jpg";
import bentoCoverage from "@/assets/bento-coverage.jpg";

const MODULES = [
  {
    icon: Newspaper,
    title: "Media Registry",
    summary: "A living directory of journalists, outlets and accreditations.",
    detail:
      "Every journalist and outlet your institution has ever engaged lives in one searchable, versioned registry, shared across your teams where appropriate but isolated from other institutions. Track affiliations, beats, seniority, contact history and accreditation status without spreadsheets.",
    img: bentoRegistry,
  },
  {
    icon: Calendar,
    title: "Events & Accreditation",
    summary: "Run press events end-to-end, invites through attendance.",
    detail:
      "Create an event, open requests, run fair and transparent selection (with geo-caps where needed), issue passes, and reconcile attendance codes at the door. Every decision is logged so you can defend it later.",
    img: bentoEvents,
  },
  {
    icon: IdCard,
    title: "Press Passes",
    summary: "QR-verifiable credentials, issued on demand.",
    detail:
      "Digital press passes that scan and verify instantly at venues, with expiry, revocation, and audit trail built in, no laminated cards, no guesswork at the door.",
  },
  {
    icon: BarChart3,
    title: "Coverage Intelligence",
    summary: "AI-scored articles, fairness signals, contribution maps.",
    detail:
      "Every submission is scored on accuracy, tone, reach and quality by AI, with full human override and an audit trail behind every score. See which journalists and outlets are actually moving the needle for your institution.",
    img: bentoCoverage,
  },
  {
    icon: Award,
    title: "Awards & Recognition",
    summary: "Stage your annual press awards end to end.",
    detail:
      "Nominations, jury scoring, shortlists, and ceremony logistics, run your institution's press awards programme without juggling five different tools.",
  },
  {
    icon: Shield,
    title: "Multi-institution Governance",
    summary: "Role-aware, audit-ready, one journalist account across every institution.",
    detail:
      "Every institution gets its own fully isolated account with row-level security enforced throughout. Roles and permissions are configurable per organisation, and every sensitive action leaves an audit trail your compliance team will actually trust.",
  },
];

const STATS = [
  { icon: Users, label: "75+ governed tables" },
  { icon: Lock, label: "RLS enforced everywhere" },
  { icon: FileBadge, label: "ISO-aligned controls" },
  { icon: Globe2, label: "EN · FR · PT" },
];

export default function Platform() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <MarketingNav />

      <section className="relative pt-32 pb-20 lg:pt-44 lg:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-grid-emerald opacity-50 [mask-image:radial-gradient(ellipse_60%_50%_at_50%_40%,black,transparent)]" aria-hidden />
        <div className="container mx-auto px-4 relative max-w-3xl">
          <span className="text-xs uppercase tracking-[0.25em] text-accent font-medium">The Platform</span>
          <h1 className="font-display text-5xl md:text-6xl mt-3 text-balance text-foreground">
            Six surfaces, one <span className="text-gradient-emerald">orbit</span>.
          </h1>
          <p className="text-justify text-muted-foreground mt-6 text-lg leading-relaxed">
            CONORBIT is the communications backbone built for institutional press operations: a registry of
            journalists and outlets, accreditation and events, press passes, coverage intelligence,
            awards, and the governance layer that holds it together. Every module is built for
            institutions that need to defend their process, not just run it.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/auth?mode=signup">
              <Button size="lg" className="gap-2 h-12 px-7">
                Request institution access <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
            <Link to="/social-suite">
              <Button size="lg" variant="outline" className="h-12 px-7">
                See the Social Suite
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4 space-y-16">
          {MODULES.map((m, i) => (
            <div key={m.title} className={`grid lg:grid-cols-2 gap-10 items-center ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}>
              <div>
                <m.icon className="w-8 h-8 text-accent mb-4" />
                <h2 className="font-display text-3xl text-foreground">{m.title}</h2>
                <p className="text-justify text-muted-foreground mt-2 text-base font-medium">{m.summary}</p>
                <p className="text-justify text-muted-foreground mt-4 leading-relaxed">{m.detail}</p>
              </div>
              {m.img ? (
                <div className="rounded-2xl overflow-hidden border border-border shadow-md">
                  <img src={m.img} alt={m.title} className="w-full h-64 object-cover" loading="lazy" />
                </div>
              ) : (
                <div className="rounded-2xl border border-border bg-card p-10 flex items-center justify-center h-64">
                  <m.icon className="w-16 h-16 text-accent/30" />
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="py-16 border-y border-border bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap items-center gap-8 justify-center">
            {STATS.map(({ icon: Icon, label }) => (
              <span key={label} className="inline-flex items-center gap-2 text-sm text-muted-foreground">
                <Icon className="w-4 h-4 text-accent" /> {label}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4 max-w-2xl text-center">
          <h2 className="font-display text-3xl md:text-4xl text-foreground">How it works</h2>
          <div className="mt-10 grid md:grid-cols-3 gap-6 text-left">
            {[
              { n: "01", title: "Onboard your institution", body: "We provision your institution with branding, governance, and your CCO. White-glove migration available." },
              { n: "02", title: "Open your journalist portal", body: "Journalists register once, get KYC'd, request press passes and submit coverage from a single doorway." },
              { n: "03", title: "Run events, score coverage", body: "Invitations, attendance codes, fair selection, post-event coverage scoring and awards, all in one orbit." },
            ].map((s) => (
              <div key={s.n} className="rounded-2xl bg-card border border-border p-6">
                <div className="font-display text-4xl text-gradient-gold leading-none">{s.n}</div>
                <h3 className="font-display text-base text-foreground mt-3">{s.title}</h3>
                <p className="text-justify text-sm text-muted-foreground mt-2 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-16">
        <div className="container mx-auto px-4 flex flex-wrap justify-center gap-6 text-sm">
          {["Built for institutions", "Unified journalist sign-in", "Audit-ready", "EN · FR · PT"].map((t) => (
            <span key={t} className="inline-flex items-center gap-2 text-muted-foreground">
              <CheckCircle2 className="w-3.5 h-3.5 text-primary-glow" /> {t}
            </span>
          ))}
        </div>
      </section>

      <MarketingFinalCta />
      <MarketingFooter />
    </div>
  );
}
